import type {ReactElement} from 'react';
import React from 'react';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {convertSecondsToMMSSFormat} from '@Utils/date.util';
import {CurrentTimeText} from '@Components/poster-editor/components/poster-seekbar/components/current-time-text';
import {getPosterDuration, updateBottomWebSeekbarState} from '@Components/poster-editor/poster-editor-reducer';
import {onPosterPause, onPosterPlay} from '@Components/poster-editor/library/poster-editor-library';
import ClickableDiv from '@Components/clickable-div/clickable-div';
import {DurationSelector} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/components/duration-selector/duration-selector';
import {
  fireGAEventForTimelineOpen,
  fireGaEventForTimelineOpenFromBottomBar,
} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.helper';
import {ElementTestId} from '@Tests/tests.types';
import styles from './play-button.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';

export function PlayButton(): ReactElement<any> | null {
  const posterDuration = useAppSelector(getPosterDuration);
  const isPlaying = useAppSelector((state) => {
    return state.posterEditor.isPlaying;
  });
  const currentTime = useAppSelector((state) => {
    return state.posterEditor.currentTime;
  });
  const isExpanded = useAppSelector((state) => {
    return state.posterEditor.isBottomWebSeekbarExpanded;
  });
  const dispatch = useAppDispatch();

  const getTotalTime = (): ReactElement<any> => {
    return <Text val={convertSecondsToMMSSFormat(posterDuration)} size={TextSize.XXSMALL} />;
  };

  const getCurrentTime = (): ReactElement<any> => {
    return <CurrentTimeText />;
  };

  const onClickPlayPause = (): void => {
    if (isPlaying) {
      onPosterPause();
    } else {
      onPosterPlay();
    }
  };

  const getDurationOption = (): ReactElement<any> => {
    return (
      <div className={`${styles.durationContainerWrapper} _overflow-hidden  radius-4 ${isExpanded ? styles.small : '_full-width'}`}>
        <ClickableDiv
          onClick={(): void => {
            if (isExpanded) {
              window.posterEditor?.whiteboard?.audioClips.unselectAudioPlaylist();
            } else {
              fireGAEventForTimelineOpen();
              fireGaEventForTimelineOpenFromBottomBar();
            }
            dispatch(updateBottomWebSeekbarState(!isExpanded));
          }}
          className={`  ${styles.durationContainer} _fit-width  ${isExpanded ? styles.selected : ''} hover-transition-all radius-4 spacing-p-l-2 spacing-p-r-2 flex-h-row flex-items-center`}
        >
          {getCurrentTime()}
          {getTotalTime()}
          <Icon className="spacing-m-l-2 spacing-m-r-1" icon="icon-pencil" size={IconSize.SIZE_ICON_16} />
          <Icon className={`spacing-m-l-2 spacing-p-l-3 spacing-m-r-1 ${isExpanded ? styles.expanded : ''} ${styles.audioIcon}`} icon="icon-audio" size={IconSize.SIZE_ICON_16} />
        </ClickableDiv>
      </div>
    );
  };

  const getDropdownForDuration = (): ReactElement<any> => {
    return <DurationSelector />;
  };

  const getIcon = (): string => {
    if (isPlaying) {
      return 'icon-pause';
    }

    if (currentTime === posterDuration) {
      return 'icon-replay-video';
    }

    return 'icon-play-video';
  };

  return (
    <div className="flex flex-row-align-center _full-width">
      <Icon
        onClick={onClickPlayPause}
        className={`spacing-m-r-2 ${styles.playPauseIcon}`}
        icon={getIcon()}
        size={IconSize.SIZE_ICON_20}
        shape={IconShape.CIRCLE}
        type={IconType.SHADOW}
        data-testid={ElementTestId.POSTER_EDITOR_WEB_BOTTOM_BAR_PLAY_PAUSE_BUTTON}
      />
      {getDurationOption()}
      {getDropdownForDuration()}
    </div>
  );
}
